import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderSpecialOutlinedIcon from '@mui/icons-material/FolderSpecialOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createSiteContent, getSiteContent, updateSiteContentId } from '../../services/api';

const saveButtonStyle = {
  bgcolor: '#7BAAA7',
  '&:hover': { bgcolor: '#6A9391' },
  color: '#000',
  textTransform: 'none',
  fontWeight: 600,
};

const DEFAULT_PROJECTS = [
  {
    funder: "FAPEAM",
    period: "2022–2024",
    title: "Recuperação e Reestruturação do CEDOMCA",
    coordinator: "Profa. Dra. Lucyanne de Melo Afonso",
    description: "Recuperação e reestruturação do Laboratório/Centro de Documentação e Memória da Cultura na Amazônia (CEDOMCA). Incluiu higienização e catalogação do acervo, criação de sistema de dados, oficinas, ciclos de palestras e eventos científicos.",
    objectives: [
      "Organização, higienização e catalogação do acervo",
      "Criação de sistema de dados digital do acervo",
      "Realização de oficinas e ciclos de palestras",
      "Promoção de eventos científicos sobre memória das artes",
    ],
    link: "",
  },
  {
    funder: "FAPEAM",
    period: "2022–2023",
    title: "Uma historiografia do ensino de música em Manaus no século XX",
    coordinator: "Profa. Dra. Lucyanne de Melo Afonso (Labem/FAARTES-UFAM)",
    description: "Estudo sobre o ensino da música em Manaus no século XX, com foco em interfaces culturais, socioculturais e políticas. Envolveu laboratórios de música, criação de plataformas digitais, oficinas, palestras, exposições e seminários.",
    objectives: [
      "Mapear e historicizar o ensino de música em Manaus",
      "Criar plataformas digitais de divulgação científica",
      "Organizar exposições, oficinas e seminários",
      "Produzir cartilhas pedagógicas e publicações científicas",
    ],
    link: "",
  },
  {
    funder: "PIBIC/PIBITI",
    period: "2021–2024",
    title: "Projetos de Iniciação Científica e Tecnológica",
    coordinator: "Profa. Dra. Lucyanne de Melo Afonso (supervisora)",
    description: "Pesquisas sobre arquivos do antigo Departamento de Artes da UFAM, mapeamento do ensino de música em diferentes regiões do Amazonas, codificação de acervos musicais e arquivísticos, além de projetos de iniciação tecnológica como jogos musicais de tabuleiro.",
    objectives: [
      "Mapear arquivos históricos do Departamento de Artes (1980–2017)",
      "Mapear o ensino de música nas mesorregiões do Amazonas",
      "Codificar acervos musicais fonográficos e arquivísticos",
      "Desenvolver jogos musicais de tabuleiro para o ensino da música",
    ],
    link: "",
  },
];

export default function EditProjectsPage() {
  const navigate = useNavigate();

  const [contentId, setContentId] = useState(null);
  const [history, setHistory] = useState('');
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [acervo, setAcervo] = useState([]);
  const [location, setLocation] = useState({ address: '', locationName: '', mapsLink: '', institutionalEmail: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    document.title = 'CEDOMCA | Editar Projetos';
    (async () => {
      try {
        const { data } = await getSiteContent();
        if (data.length > 0) {
          const item = data[0];
          setContentId(item.id);
          setHistory(item.history || '');
          setProjects(item.projects && item.projects.length > 0 ? item.projects : DEFAULT_PROJECTS);
          setContacts(item.contacts || []);
          setAcervo(item.acervo || []);
          setLocation({
            address: item.address || '',
            locationName: item.locationName || '',
            mapsLink: item.mapsLink || '',
            institutionalEmail: item.institutionalEmail || '',
          });
        } else {
          setProjects(DEFAULT_PROJECTS);
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAddProject = () =>
    setProjects((prev) => [...prev, { title: '', description: '', link: '', funder: '', period: '', coordinator: '', objectives: [] }]);

  const handleDeleteProject = (index) =>
    setProjects((prev) => prev.filter((_, i) => i !== index));

  const handleChange = (index, field, value) =>
    setProjects((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));

  // Objective management helpers
  const handleAddObjective = (projIndex) => {
    setProjects((prev) => prev.map((proj, i) => {
      if (i === projIndex) {
        const objs = proj.objectives ? [...proj.objectives] : [];
        objs.push('');
        return { ...proj, objectives: objs };
      }
      return proj;
    }));
  };

  const handleDeleteObjective = (projIndex, objIndex) => {
    setProjects((prev) => prev.map((proj, i) => {
      if (i === projIndex) {
        const objs = proj.objectives ? proj.objectives.filter((_, oi) => oi !== objIndex) : [];
        return { ...proj, objectives: objs };
      }
      return proj;
    }));
  };

  const handleChangeObjective = (projIndex, objIndex, value) => {
    setProjects((prev) => prev.map((proj, i) => {
      if (i === projIndex) {
        const objs = proj.objectives ? [...proj.objectives] : [];
        objs[objIndex] = value;
        return { ...proj, objectives: objs };
      }
      return proj;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        history,
        projects: projects
          .filter((proj) => proj.title.trim() && proj.description.trim())
          .map(({ title, description, link, funder, period, coordinator, objectives }) => ({
            title: title.trim(),
            description: description.trim(),
            ...(link && link.trim() ? { link: link.trim() } : {}),
            ...(funder && funder.trim() ? { funder: funder.trim() } : {}),
            ...(period && period.trim() ? { period: period.trim() } : {}),
            ...(coordinator && coordinator.trim() ? { coordinator: coordinator.trim() } : {}),
            objectives: (objectives || []).filter((obj) => obj.trim()).map((obj) => obj.trim()),
          })),
        contacts: contacts.map(({ name, email, phone, role }) => ({
          name,
          ...(email ? { email } : {}),
          ...(phone ? { phone } : {}),
          ...(role ? { role } : {}),
        })),
        acervo: acervo.map(({ category, period, title, description, quantity }) => ({
          category,
          period,
          title,
          description,
          quantity,
        })),
        ...(location.address ? { address: location.address } : {}),
        ...(location.locationName ? { locationName: location.locationName } : {}),
        ...(location.mapsLink ? { mapsLink: location.mapsLink } : {}),
        ...(location.institutionalEmail ? { institutionalEmail: location.institutionalEmail } : {}),
      };

      if (contentId) {
        await updateSiteContentId(contentId, payload);
      } else {
        const { data } = await createSiteContent(payload);
        setContentId(data.id);
      }
      setSnackbar({ open: true, message: 'Projetos salvos com sucesso!', severity: 'success' });
    } catch (err) {
      const raw = err?.response?.data?.message;
      const detail = Array.isArray(raw) ? raw[0] : (raw ?? 'Erro ao salvar projetos.');
      setSnackbar({ open: true, message: detail, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ minHeight: '100vh', bgcolor: '#f0f0f0' }} />;

  return (
    <Box sx={{ bgcolor: '#f0f0f0', minHeight: '100vh', p: '1.5rem' }}>
      {/* ─── Link de voltar ─── */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/site-content')}
        sx={{ textTransform: 'none', color: 'text.secondary', mb: 2, pl: 0, '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}
      >
        Voltar para seleção de seções
      </Button>

      {/* ─── Cabeçalho ─── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Editar Página de Projetos</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Cadastre, edite e remova projetos, financiamentos, coordenadores e objetivos exibidos publicamente
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={saveButtonStyle}
        >
          Salvar Alterações
        </Button>
      </Stack>

      {/* ─── Seção Projetos ─── */}
      <Paper sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FolderSpecialOutlinedIcon sx={{ color: '#2e7d32' }} />
            <Typography variant="subtitle1" fontWeight={700}>Lista de Projetos</Typography>
          </Stack>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={handleAddProject}
            sx={saveButtonStyle}
          >
            Adicionar Projeto
          </Button>
        </Stack>

        {projects.length === 0 ? (
          <Typography variant="body2" color="text.disabled" fontStyle="italic" align="center" sx={{ py: 3 }}>
            Nenhum projeto cadastrado. Clique em &quot;Adicionar Projeto&quot; para começar.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {projects.map((project, index) => (
              <Paper key={index} sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                    Projeto {index + 1}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => handleDeleteProject(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Título do Projeto</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: Recuperação e Reestruturação do CEDOMCA"
                      value={project.title}
                      onChange={(e) => handleChange(index, 'title', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Período / Ano</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: 2022–2024"
                      value={project.period || ''}
                      onChange={(e) => handleChange(index, 'period', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Financiador / Origem</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: FAPEAM, PIBIC/PIBITI"
                      value={project.funder || ''}
                      onChange={(e) => handleChange(index, 'funder', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Coordenação / Responsável</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: Profa. Dra. Lucyanne de Melo Afonso"
                      value={project.coordinator || ''}
                      onChange={(e) => handleChange(index, 'coordinator', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Link do Projeto (Opcional)</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: https://exemplo.com/projeto"
                      value={project.link || ''}
                      onChange={(e) => handleChange(index, 'link', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Descrição / Resumo do Projeto</Typography>
                    <TextField
                      fullWidth size="small" multiline rows={3}
                      placeholder="Descreva as atividades, fomento e resultados do projeto..."
                      value={project.description}
                      onChange={(e) => handleChange(index, 'description', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>

                  {/* Dynamic Objectives Sub-list */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">Objetivos do Projeto</Typography>
                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        onClick={() => handleAddObjective(index)}
                      >
                        Adicionar Objetivo
                      </Button>
                    </Stack>

                    {(!project.objectives || project.objectives.length === 0) ? (
                      <Typography variant="body2" color="text.disabled" fontStyle="italic" sx={{ pl: 1 }}>
                        Nenhum objetivo cadastrado.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {project.objectives.map((obj, objIndex) => (
                          <Stack key={objIndex} direction="row" spacing={1} alignItems="center">
                            <TextField
                              fullWidth size="small"
                              placeholder={`Objetivo ${objIndex + 1}`}
                              value={obj}
                              onChange={(e) => handleChangeObjective(index, objIndex, e.target.value)}
                            />
                            <IconButton size="small" color="error" onClick={() => handleDeleteObjective(index, objIndex)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {/* ─── Pré-visualização ─── */}
      <Paper sx={{ border: '1px solid #c8e6c9', boxShadow: 'none', borderRadius: 2, p: 2.5, mt: 3, bgcolor: '#e8f5e9' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <VisibilityOutlinedIcon sx={{ color: '#2e7d32' }} />
          <Typography variant="subtitle1" fontWeight={700} color="#2e7d32">Pré-visualização</Typography>
          <Typography variant="caption" color="#388e3c">(atualiza em tempo real)</Typography>
        </Stack>

        {projects.filter((p) => p.title.trim()).length > 0 ? (
          <Stack spacing={2}>
            {projects.filter((p) => p.title.trim()).map((project, index) => (
              <Paper key={index} sx={{ p: 2.5, border: '1px solid #c8e6c9', boxShadow: 'none', borderRadius: 2, bgcolor: '#fff' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Box sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: 10,
                    fontWeight: 'bold',
                    bgcolor: '#e8f5e9',
                    color: '#1b5e20',
                    textTransform: 'uppercase'
                  }}>
                    {project.funder || "Projeto de Pesquisa"}
                  </Box>
                  {project.period && (
                    <Typography variant="caption" color="text.secondary">
                      {project.period}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="body1" fontWeight={700} sx={{ mb: 0.5, color: '#111' }}>
                  {project.title}
                </Typography>
                {project.coordinator && (
                  <Typography variant="body2" sx={{ mb: 1, color: '#2e7d32', fontWeight: 600 }}>
                    {project.coordinator}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                  {project.description}
                </Typography>

                {project.link && (
                  <Typography variant="caption" display="block" sx={{ mb: 1.5 }}>
                    <strong>Link do Projeto:</strong>{' '}
                    <Box
                      component="a"
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: '#1565c0', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {project.link}
                    </Box>
                  </Typography>
                )}

                {project.objectives && project.objectives.filter(o => o.trim()).length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      OBJETIVOS
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.85rem', color: '#555' }}>
                      {project.objectives.filter(o => o.trim()).map((obj, objIndex) => (
                        <Box component="li" key={objIndex} sx={{ mb: 0.5 }}>
                          {obj}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="#2e7d32" fontStyle="italic" align="center" sx={{ py: 2 }}>
            Preencha os campos acima para ver a pré-visualização.
          </Typography>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert elevation={6} variant="filled" severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
